import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import ExpandedRow from '@totem/components/services/optimize/v2/optimizeDetailsTableExpandedRow';
import OptimizeRoiDisplay from '@totem/components/ticketing/details/optimize/optimizeRoiDisplay';
import OptimizeSavingsDisplay from '@totem/components/ticketing/details/optimize/optimizeSavingsDisplay';
import OptimizeStatus from '@totem/components/ticketing/details/optimize/optimizeStatus';
import TicketingContext from '@totem/components/ticketing/listContainer/ticketingContainerContext';
import StatusDisplayExtended from '@totem/components/ticketing/statusDisplayExtended';
import {
  calculateOptimizeROIValue,
  calculateTotalSavings,
  isVisibleOpportunity,
} from '@totem/components/ticketing/ticket/specialCases/utilities';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import Currency from '@totem/components/ui_components/widgets/currency/Currency';
import TagDisplay, {
  APPROVAL_STYLES,
  LEVEL_OF_EFFORT_STYLES,
  SCOPE_OF_RETURN_STYLES,
} from '@totem/components/ui_components/widgets/tagDisplay/tagDisplay';
import IBDIcon from '@totem/styles/Icon';
import { formatDate, isNotNull, isNull } from '@totem/utilities/common';

import '../../../ticketing/ticketing.css';

const styles = {
  icon: {
    fontSize: '24px',
  },
};

const OptimizeDetailsTable = () => {
  const { ticketData, loading, totalRecords, onTicketSelected } =
    useContext(TicketingContext);

  const { input, getFilterOptions, getFilterValues, handleTableChange } =
    useContext(InputContext);

  const callTicketAction = (ticket: TicketQueryResult) => {
    if (typeof onTicketSelected === 'function') {
      onTicketSelected(ticket.ticket.id, ticket);
    }
  };

  const getTicketClickAction = (ticket: TicketQueryResult) => {
    if (typeof onTicketSelected === 'function') {
      return (
        <span
          onClick={() => {
            callTicketAction(ticket);
          }}
        >
          {ticket.ticket.number}
        </span>
      );
    }

    return (
      <Link
        to={`/dashboard/optimize/opportunity/${ticket.ticket.id}?f=details`}
      >
        {ticket.ticket.number}
      </Link>
    );

    // return (
    //   <Link to={`/dashboard/optimize/ticket/${ticket.ticket.id}`}>
    //     {ticket.ticket.number}
    //   </Link>
    // );
  };

  const getControlSystemAlternate = (checkTicket: TicketQueryResult) => {
    if (
      typeof checkTicket !== 'undefined' &&
      checkTicket !== null &&
      checkTicket.ticket.attributes !== null
    ) {
      for (let idx = 0; idx < checkTicket.ticket.attributes.length; idx++) {
        const attribute = checkTicket.ticket.attributes[idx];
        if (attribute.name === 'ControlSystemType') {
          return attribute.value;
        }
      }
    }
    return '';
  };

  const getControlSystemDisplay = (checkTicket: TicketQueryResult) => {
    if (
      typeof checkTicket !== 'undefined' &&
      checkTicket !== null &&
      checkTicket.controlSystem !== null &&
      checkTicket.controlSystem.id !== null &&
      checkTicket.controlSystem.id !== EMPTY_ID
    ) {
      return <span>{checkTicket.controlSystem.name}</span>;
    } else if (
      checkTicket.controlSystem !== null &&
      checkTicket.controlSystem.name !== ''
    ) {
      return <span>{checkTicket.controlSystem.name}</span>;
    }

    return <span>{getControlSystemAlternate(checkTicket)}</span>;
  };

  const columns: ColumnProps<TicketQueryResult>[] = [
    {
      title: '',
      dataIndex: 'ticket.type',
      key: 'type',
      width: 26,
      render: (_, ticket: TicketQueryResult) => (
        <Tooltip title={ticket.ticket.type}>
          <IBDIcon name={ticket.ticket.type} style={styles.icon} />
        </Tooltip>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.type === compB.ticket.type
          ? 0
          : compA.ticket.type > compB.ticket.type
            ? 1
            : -1,
      minWidth: 20,
      filterMultiple: true,
      filteredValue: getFilterValues('type'),
      filters: getFilterOptions('type'),
      filterDropdownProps: {
        placement: 'bottom',
      },
    },
    {
      title: 'Number',
      dataIndex: 'ticket.number',
      key: 'number',
      render: (_, ticket: TicketQueryResult) => getTicketClickAction(ticket),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.number === compB.ticket.number
          ? 0
          : compA.ticket.number > compB.ticket.number
            ? 1
            : -1,
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      render: (_, ticket: TicketQueryResult) => ticket.building.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.building.name === compB.building.name
          ? 0
          : compA.building.name > compB.building.name
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('building'),
      filters: getFilterOptions('building'),
    },
    {
      title: 'Control System',
      dataIndex: 'controlSystem.name',
      key: 'controlSystem',
      render: (_, ticket: TicketQueryResult) => getControlSystemDisplay(ticket),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.controlSystem.name === compB.controlSystem.name
          ? 0
          : compA.controlSystem.name > compB.controlSystem.name
            ? 1
            : -1,
    },
    {
      title: 'Summary',
      dataIndex: 'ticket.summary',
      key: 'summary',
      render: (_, ticket: TicketQueryResult) => ticket.ticket.summary,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.summary === compB.ticket.summary
          ? 0
          : compA.ticket.summary > compB.ticket.summary
            ? 1
            : -1,
    },
    {
      title: 'Category',
      dataIndex: 'ticket.extras.optimize.category',
      key: 'category',
      render: (_, ticket: TicketQueryResult) => {
        if (
          isNull(ticket.ticket.extras) ||
          isNull(ticket.ticket.extras.optimize)
        ) {
          return <span />;
        }

        return <span>{ticket.ticket.extras.optimize.category}</span>;
      },
      sorter: (compA, compB) => {
        let compAVal = '';
        let compBVal = '';

        if (
          isNotNull(compA.ticket.extras) &&
          isNotNull(compA.ticket.extras.optimize)
        ) {
          compAVal = compA.ticket.extras.optimize.category;
        }

        if (
          isNotNull(compB.ticket.extras) &&
          isNotNull(compB.ticket.extras.optimize)
        ) {
          compBVal = compB.ticket.extras.optimize.category;
        }

        return compAVal === compBVal ? 0 : compAVal > compBVal ? 1 : -1;
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Ticket Status',
      dataIndex: 'ticket.status',
      key: 'status',
      render: (_, ticket: TicketQueryResult) => (
        <StatusDisplayExtended ticket={ticket.ticket} />
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.status === compB.ticket.status
          ? 0
          : compA.ticket.status > compB.ticket.status
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('status'),
      filters: getFilterOptions('status'),
    },
    {
      title: 'Opportunity Status',
      dataIndex: 'ticket.status',
      key: 'status',
      render: (_, ticket: TicketQueryResult) => {
        if (
          isNotNull(ticket.ticket.extras) &&
          isNotNull(ticket.ticket.extras.optimize)
        ) {
          return (
            <OptimizeStatus
              status={
                isNotNull(ticket.ticket.extras.optimize.opportunityStatus) &&
                ticket.ticket.extras.optimize.opportunityStatus !== ''
                  ? ticket.ticket.extras.optimize.opportunityStatus
                  : 'Not Set'
              }
            />
          );
        }
        return <span />;
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.status === compB.ticket.status
          ? 0
          : compA.ticket.status > compB.ticket.status
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('status'),
      filters: getFilterOptions('status'),
    },
    {
      title: 'Approval',
      dataIndex: 'ticket.approval',
      key: 'approval',
      render: (_, ticket: TicketQueryResult) => {
        if (
          isNotNull(ticket.ticket.approvalRequests) &&
          ticket.ticket.approvalRequests.length > 0
        ) {
          if (
            ticket.ticket.approvalRequests.findIndex(
              (chk) => chk.status === 'APPROVED',
            ) >= 0
          ) {
            return <TagDisplay value={'Approved'} config={APPROVAL_STYLES} />;
          }
          if (
            ticket.ticket.approvalRequests.findIndex(
              (chk) => chk.status === 'REJECTED',
            ) >= 0
          ) {
            return <TagDisplay value={'Rejected'} config={APPROVAL_STYLES} />;
          }
          if (
            ticket.ticket.approvalRequests.findIndex(
              (chk) => chk.status === 'SENT',
            ) >= 0
          ) {
            return <TagDisplay value={'Sent'} config={APPROVAL_STYLES} />;
          }
        }

        return <span />;
      },
    },
    {
      title: 'Savings Potential',
      dataIndex: 'ticket.extras.optimize.savingsPotential',
      key: 'savingsPotential',
      render: (_, ticket: TicketQueryResult) => {
        if (
          isNotNull(ticket.ticket.extras) &&
          isNotNull(ticket.ticket.extras.optimize)
        ) {
          return (
            <OptimizeSavingsDisplay optimize={ticket.ticket.extras.optimize} />
          );
        }
        return <span />;
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => {
        let compAVal = 0;
        let compBVal = 0;

        if (
          isNotNull(compA.ticket.extras) &&
          isNotNull(compA.ticket.extras.optimize)
        ) {
          compAVal = calculateTotalSavings(compA.ticket.extras.optimize);
        }

        if (
          isNotNull(compB.ticket.extras) &&
          isNotNull(compB.ticket.extras.optimize)
        ) {
          compBVal = calculateTotalSavings(compB.ticket.extras.optimize);
        }

        return compAVal === compBVal ? 0 : compAVal > compBVal ? 1 : -1;
      },
    },
    {
      title: 'Required Investment',
      dataIndex: 'ticket.extras.optimize.requiredInvestment',
      key: 'requiredInvestment',
      render: (_, ticket: TicketQueryResult) => {
        if (
          isNull(ticket.ticket.extras) ||
          isNull(ticket.ticket.extras.optimize)
        ) {
          return <span />;
        }

        return (
          <span style={{ textAlign: 'left', display: 'block' }}>
            <Currency
              amount={ticket.ticket.extras.optimize.requiredInvestment}
            />
          </span>
        );
      },
      sorter: (compA, compB) => {
        let compAVal = 0;
        let compBVal = 0;

        if (
          isNotNull(compA.ticket.extras) &&
          isNotNull(compA.ticket.extras.optimize)
        ) {
          compAVal = compA.ticket.extras.optimize.requiredInvestment;
        }

        if (
          isNotNull(compB.ticket.extras) &&
          isNotNull(compB.ticket.extras.optimize)
        ) {
          compBVal = compB.ticket.extras.optimize.requiredInvestment;
        }

        return compAVal === compBVal ? 0 : compAVal > compBVal ? 1 : -1;
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'ROI',
      dataIndex: 'ticket.extras.optimize.returnOnInvestment',
      key: 'returnOnInvestment',
      render: (_, ticket: TicketQueryResult) => {
        if (
          isNull(ticket.ticket.extras) ||
          isNull(ticket.ticket.extras.optimize)
        ) {
          return <span />;
        }

        return <OptimizeRoiDisplay optimize={ticket.ticket.extras.optimize} />;
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => {
        let compAVal = 0;
        let compBVal = 0;

        if (
          isNotNull(compA.ticket.extras) &&
          isNotNull(compA.ticket.extras.optimize)
        ) {
          compAVal = calculateOptimizeROIValue(compA.ticket.extras.optimize);
        }

        if (
          isNotNull(compB.ticket.extras) &&
          isNotNull(compB.ticket.extras.optimize)
        ) {
          compBVal = calculateOptimizeROIValue(compB.ticket.extras.optimize);
        }

        return compAVal === compBVal ? 0 : compAVal > compBVal ? 1 : -1;
      },
    },
    {
      title: 'Level of Effort',
      dataIndex: 'ticket.extras.optimize.levelOfEffort',
      key: 'levelOfEffort',
      render: (_, ticket: TicketQueryResult) => {
        if (
          isNull(ticket.ticket.extras) ||
          isNull(ticket.ticket.extras.optimize)
        ) {
          return <span />;
        }

        return (
          <TagDisplay
            value={ticket.ticket.extras.optimize.levelOfEffort}
            config={LEVEL_OF_EFFORT_STYLES}
          />
        );
      },
      sorter: (compA, compB) => {
        let compAVal = '';
        let compBVal = '';

        if (
          isNotNull(compA.ticket.extras) &&
          isNotNull(compA.ticket.extras.optimize)
        ) {
          compAVal = compA.ticket.extras.optimize.levelOfEffort;
        }

        if (
          isNotNull(compB.ticket.extras) &&
          isNotNull(compB.ticket.extras.optimize)
        ) {
          compBVal = compB.ticket.extras.optimize.levelOfEffort;
        }

        return compAVal === compBVal ? 0 : compAVal > compBVal ? 1 : -1;
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Scope of Return',
      dataIndex: 'ticket.extras.optimize.scopeOfReturn',
      key: 'scopeOfReturn',
      render: (_, ticket: TicketQueryResult) => {
        if (
          isNull(ticket.ticket.extras) ||
          isNull(ticket.ticket.extras.optimize)
        ) {
          return <span />;
        }

        return (
          <TagDisplay
            value={ticket.ticket.extras.optimize.scopeOfReturn}
            config={SCOPE_OF_RETURN_STYLES}
          />
        );
      },
      sorter: (compA, compB) => {
        let compAVal = '';
        let compBVal = '';

        if (
          isNotNull(compA.ticket.extras) &&
          isNotNull(compA.ticket.extras.optimize)
        ) {
          compAVal = compA.ticket.extras.optimize.scopeOfReturn;
        }

        if (
          isNotNull(compB.ticket.extras) &&
          isNotNull(compB.ticket.extras.optimize)
        ) {
          compBVal = compB.ticket.extras.optimize.scopeOfReturn;
        }

        return compAVal === compBVal ? 0 : compAVal > compBVal ? 1 : -1;
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    // {
    //   title: 'Related',
    //   dataIndex: 'ticket.relatedTickets',
    //   key: 'summary',
    //   render: (_, ticket: TicketQueryResult) =>
    //     isNull(ticket.relatedTickets) ? (
    //       <span />
    //     ) : (
    //       ticket.relatedTickets.map((rel) => (
    //         <div key={rel.ticket.ticket.id}>
    //           <span title={rel.relationship}>
    //             {rel.relationship.toUpperCase().charAt(0)}
    //           </span>
    //           :{' '}
    //           <Link to={`/dashboard/ticket/${rel.ticket.ticket.id}`}>
    //             {rel.ticket.ticket.number}
    //           </Link>
    //         </div>
    //       ))
    //     ),
    // },
    {
      title: 'Created',
      dataIndex: 'ticket.createdAt',
      key: 'createdAt',
      render: (_, ticket: TicketQueryResult) =>
        formatDate(ticket.ticket.createdAt),
      sorter: (compA, compB) =>
        compA.ticket.createdAt === compB.ticket.createdAt
          ? 0
          : compA.ticket.createdAt > compB.ticket.createdAt
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Updated',
      dataIndex: 'ticket.updatedAt',
      key: 'updatedAt',
      render: (_, ticket: TicketQueryResult) =>
        formatDate(ticket.ticket.updatedAt),
      sorter: (compA, compB) =>
        compA.ticket.updatedAt === compB.ticket.updatedAt
          ? 0
          : compA.ticket.updatedAt > compB.ticket.updatedAt
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
  ];

  const getTickets = () =>
    (ticketData?.tickets ?? []).filter(isVisibleOpportunity);

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getTickets()}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.ticket.id}
      expandable={{
        expandedRowRender: (record: TicketQueryResult) => (
          <ExpandedRow record={record} />
        ),
        rowExpandable: () => true,
      }}
    />
  );
};

export default OptimizeDetailsTable;
